import { appLocalStorage, storageName } from '../utils/storage';
import { getUser } from '../utils/func';
import { fullOpinionKey, pendingOpinionKey } from '../pages/reaction/ReactionEditor/opinionDraft';

const JOURNAL_TTL = 7 * 24 * 60 * 60 * 1000;
export const RECOVERY_JOURNAL_LIMITS = Object.freeze({ count: 10, bytes: 2 * 1024 * 1024, ttl: JOURNAL_TTL });
const fail = code => { const error = new Error(code); error.code = code; throw error; };
const expiry = (draft, now) => {
  const server = Date.parse(draft.expires_at);
  return Number.isFinite(server) ? Math.min(server, now + JOURNAL_TTL) : now + JOURNAL_TTL;
};

// Only delete this account's expired recovery journals. Editable full/pending
// comments and other accounts' journals are not part of this cleanup.
export function pruneRecoveryJournals(userId, now = Date.now(), activeDrafts = []) {
  if (!userId || getUser()?.id !== userId) fail('ACCOUNT_CHANGED');
  const prefix = `protection-restore:${userId}:`;
  const physicalPrefix = storageName(prefix);
  const keys = [];
  for (let index = 0; index < window.localStorage.length; index += 1) {
    const key = window.localStorage.key(index);
    if (key?.startsWith(physicalPrefix)) keys.push(prefix + key.slice(physicalPrefix.length));
  }
  return keys.flatMap(key => {
    let raw = appLocalStorage.getItem(key);
    if (!raw) return [];
    let value;
    try { value = JSON.parse(raw); } catch { /* Retain an unreadable marker; never assume it was not applied. */ }
    const current = activeDrafts.find(draft => `${prefix}${draft.id}` === key);
    // A rejected edit can renew the same server draft. Retain the applied
    // marker until that current revision expires, not its first attempt's date.
    const currentExpiry = current && expiry(current, now);
    if (value && currentExpiry > now && value.expiresAt !== currentExpiry) {
      value = { ...value, expiresAt: currentExpiry };
      raw = JSON.stringify(value);
      appLocalStorage.setItem(key, raw);
    }
    const expiresAt = Number.isFinite(value?.expiresAt) ? value.expiresAt
      : Number.isFinite(value?.createdAt) ? value.createdAt + JOURNAL_TTL : null;
    if (expiresAt !== null && expiresAt <= now) {
      appLocalStorage.removeItem(key);
      return [];
    }
    return [{ key, bytes: new Blob([raw]).size }];
  });
}

function writeJournal(userId, key, value) {
  const raw = JSON.stringify(value);
  const others = pruneRecoveryJournals(userId).filter(entry => entry.key !== key);
  if (others.length >= RECOVERY_JOURNAL_LIMITS.count
    || others.reduce((sum, entry) => sum + entry.bytes, new Blob([raw]).size) > RECOVERY_JOURNAL_LIMITS.bytes) {
    fail('RECOVERY_STORAGE_LIMIT');
  }
  // Never evict an unexpired applied marker to fit a new draft: that would
  // allow a subsequent click to append the same article twice.
  appLocalStorage.setItem(key, raw);
}

function authoredContent(html) {
  const template = document.createElement('template');
  template.innerHTML = html;
  template.content.querySelectorAll('div[data-wiw-quote="true"]').forEach(node => node.remove());
  template.content.querySelectorAll('span[data-recorded-media-id]').forEach(node => {
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(node.getAttribute('data-recorded-media-id') || '')) node.remove();
  });
  return template;
}

function hasAuthoredSuffix(before, content) {
  const source = Array.from(content.content.childNodes);
  if (!source.length) return true;
  const previous = Array.from(authoredContent(before).content.childNodes);
  if (previous.length < source.length) return false;
  // Compare complete trailing nodes. A word at the end of a different text
  // node is not proof that this draft has already been restored.
  return source.every((node, index) => node.isEqualNode(previous[previous.length - source.length + index]));
}

// Keep the server copy and a local journal. A restore only opens an editable
// draft, never publishes it, and never replaces independently typed text.
export function recoverProtectionDraft(userId, draft, activeDrafts = [draft]) {
  if (!userId || getUser()?.id !== userId || !draft?.id || !draft.payload) throw new Error('Account changed');
  const now = Date.now();
  const expiresAt = expiry(draft, now);
  pruneRecoveryJournals(userId, now, activeDrafts);
  if (expiresAt <= now) fail('RECOVERY_DRAFT_EXPIRED');
  const key = `protection-restore:${userId}:${draft.id}`;
  const priorRaw = appLocalStorage.getItem(key);
  const prior = priorRaw ? JSON.parse(priorRaw) : null;
  const payload = draft.payload;
  if (draft.kind === 'story' && typeof payload.title === 'string') {
    writeJournal(userId, key, { payload, createdAt: now, expiresAt });
    return { path: '/news/add', state: { protectionDraft: { userId, id: draft.id, title: payload.title } } };
  }
  if (draft.kind !== 'reaction' || !Number.isSafeInteger(payload.story_id) || payload.story_id <= 0) throw new Error('Invalid draft');
  const full = fullOpinionKey(userId, payload.story_id);
  const pending = pendingOpinionKey(userId, payload.story_id);
  const target = appLocalStorage.getItem(full) !== null ? full : pending;
  const sameContent = !prior?.payload || (prior.payload.txt_message || '') === (payload.txt_message || '');
  if (!prior?.applied || !sameContent) {
    const template = authoredContent(typeof payload.txt_message === 'string' ? payload.txt_message : '');
    // Recordings and quotes need their live attachment state checked in the
    // editor. Their complete source metadata stays in the recovery journal.
    const html = template.innerHTML;
    const before = appLocalStorage.getItem(target) || '';
    const alreadyWritten = hasAuthoredSuffix(before, template) || (sameContent && ((prior?.target === target && before === prior.merged)
      || (prior?.target === pending && target === full && prior.merged && before.endsWith(prior.merged))));
    const merged = alreadyWritten ? before : before + html;
    const journal = { payload, target, merged, createdAt: now, expiresAt, applied: false };
    writeJournal(userId, key, journal);
    appLocalStorage.setItem(target, merged);
    writeJournal(userId, key, { ...journal, applied: true });
  }
  return { path: `/news/${payload.story_id}/opinion`, state: { protectionRestore: true, protectionRestoreScore: payload.score } };
}
